import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"
import type Video from "@/types/serializers/Video"
import type Listing from "@/types/serializers/Listing"
import type Script from "@/types/serializers/Script"
import type Render from "@/types/serializers/Render"
import type Account from "@/types/serializers/Account"
import type SocialConnection from "@/types/serializers/SocialConnection"
import AdminLayout from "@/components/admin/layout"
import AdminPostDialog from "@/components/admin/admin-post-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { OptimizedImage } from "@/components/ui/optimized-image"

interface CostSummary {
  total_cost_cents: number
  by_job_type: Record<string, number>
  by_provider: Record<string, number>
}

interface Props {
  video: Video
  listing: Listing
  account: Account
  owner_name: string
  owner_id: number
  scripts: Script[]
  renders: Render[]
  cost_summary: CostSummary
  admin_connections: SocialConnection[]
}

const PROVIDER_COLOURS: Record<string, string> = {
  anthropic: "#D4A853",
  elevenlabs: "#60A5FA",
  fal: "#34D399",
  remotion: "#A78BFA",
  aws: "#F97316",
  kernel: "#F472B6",
  internal: "#6B6B6B",
}

const JOB_TYPE_COLOURS: Record<string, string> = {
  scrape: "#F472B6",
  extract: "#D4A853",
  classify_images: "#D4A853",
  script: "#D4A853",
  voiceover: "#60A5FA",
  music: "#60A5FA",
  enhance_image: "#34D399",
  animate_clip: "#34D399",
  render: "#A78BFA",
  copy_video: "#F97316",
}

const STATUS_COLOURS: Record<string, string> = {
  draft: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  scripted: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  rendered: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  published: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  archived: "bg-cream/10 text-cream/60 border-cream/20",
}

const RENDER_STATUS_COLOURS: Record<string, string> = {
  pending: "bg-cream/10 text-cream/60 border-cream/20",
  rendering: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  failed: "bg-red/10 text-red border-red/20",
  timed_out: "bg-red/10 text-red border-red/20",
}

const SCRIPT_STATUS_COLOURS: Record<string, string> = {
  draft: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  archived: "bg-cream/10 text-cream/60 border-cream/20",
}

const TEMPLATE_LABELS: Record<string, string> = {
  luxury: "Luxury",
  modern: "Modern",
  classic: "Classic",
  bold: "Bold",
}

const TRANSITION_LABELS: Record<string, string> = {
  fade: "Fade",
  slide: "Slide",
  zoom: "Zoom",
  cut: "Cut",
}

export default function AdminVideoShow({
  video,
  listing,
  account,
  owner_name,
  owner_id,
  scripts,
  renders,
  cost_summary,
  admin_connections,
}: Props) {
  const activeScript = scripts.find((s) => s.active)
  const [watchUrl, setWatchUrl] = useState<string | null>(null)
  const [postDialogOpen, setPostDialogOpen] = useState(false)
  const completedRenders = renders.filter((r) => r.status === "completed" && r.video_url)
  const canPost = completedRenders.length > 0 || listing.images_data.length > 0

  return (
    <>
    <AdminLayout>
      <Head title={`${video.name} — Admin`} />

      {/* Back link */}
      <Link
        href="/admin/videos"
        className="mb-6 inline-flex items-center gap-1 text-sm text-cream/50 hover:text-cream"
      >
        <svg
          className="size-4"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
        </svg>
        Back to videos
      </Link>

      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-cream">{video.name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge className={STATUS_COLOURS[video.status]}>{video.status}</Badge>
          </div>
        </div>
        {canPost && (
          <Button
            size="sm"
            className="bg-red text-white hover:bg-deep-red"
            onClick={() => setPostDialogOpen(true)}
          >
            <svg className="mr-1.5 size-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
            </svg>
            Post
          </Button>
        )}
      </div>

      {/* Account / Owner card */}
      <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1 text-sm">
            <p className="text-cream/40">
              Account: <span className="text-cream/70">{account.name}</span>
            </p>
            <p className="text-cream/40">
              Owner: <span className="text-cream/70">{owner_name}</span>
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-white/20 text-cream hover:bg-white/10"
              asChild
            >
              <Link href={`/admin/listings/${listing.id}`}>View listing</Link>
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-white/20 text-cream hover:bg-white/10"
              onClick={() => router.post(`/admin/users/${owner_id}/impersonate`)}
            >
              Impersonate
            </Button>
          </div>
        </div>
      </div>

      {/* Settings card */}
      <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
          Settings
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailItem label="Aspect ratio" value={video.aspect_ratio} />
          <DetailItem label="Template" value={TEMPLATE_LABELS[video.template_variant] || video.template_variant} />
          <DetailItem label="Length" value={`${video.target_length}s`} />
          <DetailItem label="Transition" value={TRANSITION_LABELS[video.transition_style] || video.transition_style} />
          <DetailItem label="Language" value={video.language.toUpperCase()} />
          <DetailItem label="Tone" value={video.tone || "\u2014"} />
          {video.target_audience && (
            <DetailItem label="Audience" value={video.target_audience.replace(/-/g, " ")} />
          )}
        </div>
      </div>

      {/* Script summary */}
      <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
          Script ({scripts.length} {scripts.length === 1 ? "version" : "versions"})
        </h2>
        {activeScript ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge className={SCRIPT_STATUS_COLOURS[activeScript.status] || SCRIPT_STATUS_COLOURS.draft}>
                {activeScript.status}
              </Badge>
              <span className="text-xs text-cream/40">v{activeScript.version}</span>
              {activeScript.active && (
                <Badge className="border-emerald-500/20 bg-emerald-500/10 text-emerald-400">
                  active
                </Badge>
              )}
            </div>
            {activeScript.content && typeof activeScript.content === "object" && (
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-black/20 p-3 text-xs leading-relaxed text-cream/70">
                {JSON.stringify(activeScript.content, null, 2)}
              </pre>
            )}
          </div>
        ) : (
          <p className="text-sm text-cream/30">No active script.</p>
        )}
      </div>

      {/* Audio summary */}
      <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
          Audio
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailItem label="Voice" value={video.voice || "Not set"} />
          <DetailItem label="Music status" value={video.music_status} />
          {video.voice_speed != null && (
            <DetailItem label="Voice speed" value={`${video.voice_speed}x`} />
          )}
        </div>
      </div>

      {/* Scene images grid */}
      {video.scene_assets.length > 0 && (
        <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
          <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
            Scene assets ({video.scene_assets.length})
          </h2>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
            {video.scene_assets.map((asset) => (
              <div
                key={asset.id}
                className="overflow-hidden rounded bg-white/5"
              >
                {asset.file_url ? (
                  <OptimizedImage
                    src={asset.file_url}
                    alt={`Scene ${asset.scene_index}`}
                    className="aspect-video w-full object-cover"
                  />
                ) : (
                  <div className="flex aspect-video items-center justify-center text-xs text-cream/20">
                    No image
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Render history */}
      <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
          Renders ({renders.length})
        </h2>
        {renders.length === 0 ? (
          <p className="text-sm text-cream/30">No renders yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="border-b border-white/[0.06] text-left">
                  <th className="px-3 py-2 text-xs font-medium uppercase tracking-wider text-cream/40">
                    Status
                  </th>
                  <th className="px-3 py-2 text-xs font-medium uppercase tracking-wider text-cream/40">
                    Template
                  </th>
                  <th className="px-3 py-2 text-xs font-medium uppercase tracking-wider text-cream/40">
                    Created
                  </th>
                  <th className="px-3 py-2 text-xs font-medium uppercase tracking-wider text-cream/40">
                    Video
                  </th>
                </tr>
              </thead>
              <tbody>
                {renders.map((render) => (
                  <tr
                    key={render.id}
                    className="border-b border-white/[0.04] text-cream/60"
                  >
                    <td className="whitespace-nowrap px-3 py-2.5">
                      <Badge className={RENDER_STATUS_COLOURS[render.status] || RENDER_STATUS_COLOURS.pending}>
                        {render.status.replace("_", " ")}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-cream/50">
                      {TEMPLATE_LABELS[render.template_variant] || render.template_variant}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-cream/40">
                      {formatDate(render.created_at)}
                    </td>
                    <td className="px-3 py-2.5">
                      {render.video_url ? (
                        <button
                          type="button"
                          className="text-sm text-blue-400 hover:underline"
                          onClick={(e) => {
                            e.stopPropagation()
                            setWatchUrl(render.video_url!)
                          }}
                        >
                          Watch
                        </button>
                      ) : (
                        <span className="text-xs text-cream/30">{"\u2014"}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cost breakdown */}
      <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
          Cost breakdown
        </h2>
        <p className="mb-4 text-2xl font-semibold tabular-nums text-cream">
          {formatCurrency(cost_summary.total_cost_cents)}
        </p>
        {cost_summary.total_cost_cents > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <CostBreakdown title="By job type" data={cost_summary.by_job_type} colours={JOB_TYPE_COLOURS} />
            <CostBreakdown title="By provider" data={cost_summary.by_provider} colours={PROVIDER_COLOURS} />
          </div>
        ) : (
          <p className="text-sm text-cream/30">No costs recorded.</p>
        )}
      </div>

      {/* Branding */}
      <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
          Branding
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <DetailItem label="CTA" value={video.cta_override || "Default"} />
          <DetailItem label="Show logo" value={video.show_logo ? "Yes" : "No"} />
        </div>
      </div>
    </AdminLayout>

    {/* Admin post dialog */}
    <AdminPostDialog
      videoId={video.id}
      renders={renders}
      listing={listing}
      connections={admin_connections}
      open={postDialogOpen}
      onOpenChange={setPostDialogOpen}
    />

    {/* Video player dialog */}
    <Dialog open={!!watchUrl} onOpenChange={(open) => { if (!open) setWatchUrl(null) }}>
      <DialogContent className="dark max-w-3xl border-white/10 bg-charcoal p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="text-cream">Video preview</DialogTitle>
        </DialogHeader>
        {watchUrl && (
          <div className="px-6 pb-6">
            <video
              src={watchUrl}
              controls
              autoPlay
              className="w-full rounded-lg"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  )
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-cream/40">{label}</p>
      <p className="mt-1 text-sm font-medium capitalize text-cream/80">{value}</p>
    </div>
  )
}

function CostBreakdown({ title, data, colours }: { title: string; data: Record<string, number>; colours: Record<string, string> }) {
  const entries = Object.entries(data)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
  const total = entries.reduce((sum, e) => sum + e.value, 0)

  return (
    <div>
      <p className="mb-2 text-xs font-medium uppercase tracking-wider text-cream/30">{title}</p>
      <div className="space-y-1.5">
        {entries.map((entry) => {
          const pct = total > 0 ? (entry.value / total) * 100 : 0
          const colour = colours[entry.name] || "#6B6B6B"
          return (
            <div key={entry.name}>
              <div className="mb-0.5 flex items-center justify-between text-xs">
                <span className="text-cream/60">{formatLabel(entry.name)}</span>
                <span className="tabular-nums text-cream/40">{formatCurrency(entry.value)}</span>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-white/[0.06]">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, backgroundColor: colour }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function formatCurrency(cents: number): string {
  const dollars = cents / 100
  if (dollars >= 1) return `$${dollars.toFixed(2)}`
  return `${cents}c`
}

function formatLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatDate(dateStr: string | Date): string {
  const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}
